import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environment/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  AccountRequestType,
  AccountResponseType,
  IAccount,
  IAccountByIdResponseObject,
  IAccountLogs,
  IAccountLogsResponseType,
  ISaveContactResponse,
  ISaveResponseType,
} from './models/account.model';
import {
  IInvoiceByIdResponseObject,
  IInvoiceLogDataResponse,
  IInvoiceResponseObject,
  Invoice,
  MutationEntryLine,
} from './models/invoice.model';
import { DateConverterService } from '../../helpers/date-converter.service';
import { restEndPoints } from '../../app.constants';

@Injectable({
  providedIn: 'root',
})
export class AccountingService {
  apiHost = environment.apiHost;
  endPoints = restEndPoints.accounting;

  private _invoiceData = new BehaviorSubject<Invoice | null>(null);
  public readonly invoiceDataById$: Observable<Invoice | null> =
    this._invoiceData.asObservable();

  private _accountData = new BehaviorSubject<IAccount | null>(null);
  public readonly accountDataById$: Observable<IAccount | null> =
    this._accountData.asObservable();

  constructor(
    private _http: HttpClient,
    private _dateServc: DateConverterService,
  ) {
    this.loadDataFromSession('accountData', this._accountData);
    this.loadDataFromSession('invoiceData', this._invoiceData);
  }

  loadDataFromSession(key: string, subject: BehaviorSubject<any>): void {
    const storedData = sessionStorage.getItem(key);
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        subject.next(parsedData);
      } catch (e) {
        console.error(`Error parsing stored data for key "${key}":`, e);
        sessionStorage.removeItem(key);
      }
    }
  }

  getContactsData(params: AccountRequestType): Observable<AccountResponseType> {
    const url = `${this.apiHost}${this.endPoints[0].contacts.getContactsData}`;
    return this._http.post<AccountResponseType>(url, params);
  }

  // getContactsCount(startDate: any,endDate:any): Observable<any[]> {
  //   const url = `${this.apiHost}${this.endPoints[0].contacts.getContactsCount}?startDate=2025-08-14T12:49:34.282Z&endDate=2025-08-14T12:49:34.282Z`;
  //   return this._http.post<any[]>(url,{});
  // }

  clearAccountData() {
    this._accountData.next(null);
    sessionStorage.removeItem('accountData');
  }

  getContactsById(id: number): Observable<IAccountByIdResponseObject> {
    const url = `${this.apiHost}${this.endPoints[0].contacts.getContactsById}/${id}`;
    return this._http.get<IAccountByIdResponseObject>(url);
  }

  saveContact(contact: ISaveContactResponse): Observable<ISaveResponseType> {
    const url = `${this.apiHost}${this.endPoints[0].contacts.saveContact}`;
    return this._http.post<ISaveResponseType>(url, contact);
  }

  deleteContact(id: number): Observable<number> {
    const url = `${this.apiHost}${this.endPoints[0].contacts.deleteContact}?profileId=${id}`;
    return this._http.post<number>(url, {}, { responseType: 'json' });
  }

  getContactLogs(id: number): Observable<IAccountLogsResponseType> {
    const url = `${this.apiHost}${this.endPoints[0].contacts.getContactLogs}/${id}`;
    return this._http.get<IAccountLogsResponseType>(url);
  }

  updateContactLogData(body: any) {
    const url = `${this.apiHost}${this.endPoints[0].contacts.updateContactLogData}`;
    return this._http.post<any>(url, {
      id: body.id,
      profileId: body.profileId,
      comment: body.comment,
    });
  }

  importAccountingData(id: number[]): Observable<number[]> {
    const url = `${this.apiHost}${this.endPoints[0].contacts.importAccountingData}`;
    return this._http.post<number[]>(url, id);
  }

  /**
   * @param Invoice Data
   * @returns
   */

  getInvoiceData(params: any): Observable<IInvoiceResponseObject> {
    params.startDate = this._dateServc.getTimezoneAgnosticDateIsoString(
      params.startDate,
    );
    params.endDate = this._dateServc.getTimezoneAgnosticDateIsoString(
      params.endDate,
    );
    const url = `${this.apiHost}${this.endPoints[0].invoice.getInvoiceData}`;
    return this._http.post<IInvoiceResponseObject>(url, params);
  }

  getMutationEntriesByFinanceMutationId(
    financeMutationId: number,
  ): Observable<Invoice[]> {
    const url = `${this.apiHost}${this.endPoints[0].invoice.getMutationEntriesByFinanceMutationId}/${financeMutationId}`;
    return this._http.get<Invoice[]>(url);
  }

  getFinanceEntryLinesByFinanceMutationId(
    id: number,
  ): Observable<MutationEntryLine[]> {
    const url = `${this.apiHost}${this.endPoints[0].invoice.getMutationEntriesByFinanceMutationId}${id}`;
    return this._http.get<MutationEntryLine[]>(url);
  }

  getInvoiceLogs(
    id: number,
    pageNumber: number,
    pageSize: number,
  ): Observable<IInvoiceLogDataResponse> {
    const url = `${this.apiHost}${this.endPoints[0].invoice.getInvoiceLogs}/${id}/${pageNumber}/${pageSize}`;
    return this._http.get<IInvoiceLogDataResponse>(url);
  }

  getInvoiceById(invoiceId: number): Observable<any> {
    const url = `${this.apiHost}${this.endPoints[0].invoice.getInvoiceById}?id=${invoiceId}`;
    return this._http.get<IInvoiceByIdResponseObject>(url);
  }

  saveEolContactId(id: number, eolContactId: string): Observable<any> {
    const url = `${this.apiHost}${this.endPoints[0].invoice.saveEolContactId}/?id=${id}&eolContactId=${eolContactId}`;
    return this._http.post<any>(url, {});
  }

  updateInvoiceLogData(body: any) {
    const url = `${this.apiHost}${this.endPoints[0].invoice.updateInvoiceLogData}`;
    return this._http.post<any>(url, body);
  }

  importInvoiceData(id: number[]): Observable<number[]> {
    const url = `${this.apiHost}${this.endPoints[0].invoice.importInvoiceData}`;
    return this._http.post<number[]>(url, id);
  }

  clearInvoiceData() {
    this._accountData.next(null);
    sessionStorage.removeItem('accountData');
  }
}
